import { motion } from 'framer-motion';
import styled from 'styled-components';

import { floorGameColor as color, styledInteraction, styledLayout, styledMedia, styledReset } from '@pkg/primitives';
import { percentToHex } from '@pkg/utils';

import { tokens } from '../../tokens';

import {
    labelTransition,
    labelVariants,
    ornamentTransition,
    ornamentVariants,
    PriceGameSharedVariant,
    priceGameVariants,
} from './PriceGame.motion';

interface StyledPriceGameProps {
    $compact?: boolean;
}

interface StyledInteriorProps {
    $labelSpacing?: boolean;
}

interface StyledColumnProps {
    $currencyOffset?: boolean;
}

const yellowAlpha6 = `${color.yellow}${percentToHex(6)}`;

export const StyledPriceGame = styled(motion.div).attrs({
    variants: priceGameVariants,
    initial: PriceGameSharedVariant.Initial,
    animate: PriceGameSharedVariant.Show,
    exit: PriceGameSharedVariant.Exit,
})<StyledPriceGameProps>`
    --yat_price-game-min-height: ${({ $compact }) =>
        tokens.card.children.minHeight[$compact ? 'compact' : 'default']}px;
    --yat_price-game-interior-gap: ${({ $compact }) =>
        tokens.priceGame.interior.gap[$compact ? 'compact' : 'default']}px;

    z-index: ${tokens.priceGame.zindex.base};
    position: relative;
    display: grid;
    align-items: end;
    grid-area: 1 / 1;
    // Required in order to prevent layout shifting.
    min-height: var(--yat_price-game-min-height);

    &::before {
        ${styledInteraction.interactionDisable};
        ${styledLayout.positionCover()};
        content: '';
        display: block;
        z-index: ${tokens.priceGame.zindex.glow};
        border-radius: ${tokens.card.wrapper.radius}px;
        background: linear-gradient(to bottom, transparent, transparent, ${yellowAlpha6});
    }
`;

export const CurrencyBackground = styled.div`
    ${styledInteraction.interactionDisable};
    ${styledLayout.positionCover()};
    z-index: ${tokens.priceGame.zindex.currencyBg};
    overflow: hidden;
    // These blend styles may not work for every
    // logo combination, and could impact performance.
    mix-blend-mode: color-dodge;
    // mix-blend-mode: overlay;
    opacity: 0.1;
`;

export const CurrencyLogo = styled.div`
    ${styledMedia.iconWrapper};
    // We might want to make the size percentage based
    // relative to the parent's height.
    width: ${tokens.priceGame.currencyBg.size}px;
    height: ${tokens.priceGame.currencyBg.size}px;
    // This positioning may not work for every logo combination.
    translate: 25% -10%;
`;

export const OrnamentWrapper = styled(motion.div).attrs({
    variants: ornamentVariants,
    transition: ornamentTransition,
})`
    ${styledInteraction.interactionDisable};
    z-index: ${tokens.zindex.ornament};
    position: absolute;
    top: 0;
    left: 10%;
    right: 10%;
    transform: translateY(-50%);
`;

export const LabelWrapper = styled(motion.div).attrs({
    variants: labelVariants,
    transition: labelTransition,
})`
    z-index: ${tokens.zindex.label};
    position: absolute;
    top: -${tokens.priceGame.label.offsetY}px;
    left: ${tokens.priceGame.label.offsetX}px;
`;

export const Interior = styled.div<StyledInteriorProps>`
    z-index: ${tokens.priceGame.zindex.interior};
    position: relative;
    display: grid;
    align-content: end;
    gap: var(--yat_price-game-interior-gap);
    padding: ${tokens.card.children.padding}px;
    padding-top: ${({ $labelSpacing }) => ($labelSpacing ? `${tokens.priceGame.interior.paddingTop}px` : undefined)};
`;

export const Columns = styled.div`
    display: grid;
    grid-auto-flow: column;
    align-items: center;
    justify-content: space-between;
    gap: ${tokens.priceGame.column.gap}px;
`;

export const Column = styled.div<StyledColumnProps>`
    display: grid;
    grid-auto-flow: column;
    align-items: center;
    gap: ${tokens.priceGame.column.gap}px;
    // Better visual alignment when <CurrencyDisplay />
    // renders a "currency symbol".
    margin-left: ${({ $currencyOffset }) => ($currencyOffset ? `${tokens.priceGame.column.offset * -1}px` : undefined)};
`;

export const ThumbnailStackWrapper = styled.div`
    // We want the "bottom NFTs" to sit slightly outside of the grid.
    // TODO: This is debateable, as we have to also check against 'items.length'
    // and it could cause problems with "shifting".
    margin-right: -${tokens.priceGame.thumbnails.offset}px;
`;

export const MaxTier = styled.div`
    display: grid;
    place-content: center;
    place-items: center;
    width: ${tokens.priceGame.maxTier.width}px;
    height: ${tokens.priceGame.maxTier.height}px;
`;

export const MaxTierEmoji = styled.p`
    ${styledReset.typography};
    font-size: ${tokens.priceGame.maxTier.fontSize}px;
`;
